import * as React from 'react';
import styled from 'styled-components';
import { isDownArrow, isUpArrow, isLeftArrow, isRightArrow } from '@starrez/starryui9';
import { IRadioButtonItemProps } from './RadioButtonItem';
import { useEffect } from 'react';

export type StackOrientation = 'Vertical' | 'Horizontal';

export interface IRadioButtonProps {
    onChange: (item: string) => void;
    value?: number | string;
    compact?: boolean;
    stackOrientation?: StackOrientation;
}

export const RadioButton: React.FC<IRadioButtonProps> = (props) => {
    const divContainer = React.createRef<HTMLDivElement>();

    useEffect(() => {
        if (props.value) {
            setAsSelected(props.value);
        } else {
            const value = divContainer.current?.firstElementChild?.firstElementChild?.getAttribute('data-value');
            setAsSelected(value || "");
        }
    }, []);

    const setAsSelected = (value: string | number) => {
        React.Children.map(props.children as React.ReactElement, (child: React.ReactElement<IRadioButtonItemProps>, index) => {
            if (child.props.value.toString() === value.toString()) {
                divContainer.current?.children[index].firstElementChild?.setAttribute('aria-checked', 'true');
                divContainer.current?.children[index].firstElementChild?.setAttribute('tabindex', '0');
            } else {
                divContainer.current?.children[index].firstElementChild?.setAttribute('aria-checked', 'false');
                divContainer.current?.children[index].firstElementChild?.setAttribute('tabindex', '-1');
            }
        });
    };

    const handleOnClick = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();

        const target = e.target as HTMLElement;
        if (
            target.getAttribute('role') !== 'radiogroup' &&
            target.getAttribute('data-children') !== 'children' &&
            target.getAttribute('role') === 'radio'
        ) {
            setAsSelected(target.getAttribute('data-value') || "");
            props.onChange(target.getAttribute('data-value') || "");
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        e.stopPropagation();
        const target = e.target as HTMLElement;
        const targetParent = target.parentElement as HTMLElement;

        if ((isDownArrow(e) && props.stackOrientation === 'Vertical') || (isRightArrow(e) && props.stackOrientation === 'Horizontal')) {
            // we search for the next sibling which is not disabled
            let targetFound = false;
            let nextTarget = targetParent.nextSibling;
            while (nextTarget && !targetFound) {
                const thisTarget = nextTarget.firstChild as HTMLInputElement;
                if (!thisTarget.disabled) {
                    targetFound = true;
                    thisTarget.focus();
                    setAsSelected(thisTarget.getAttribute('data-value') || "");
                    props.onChange(thisTarget.getAttribute('data-value') || "");
                } else {
                    nextTarget = nextTarget.nextSibling;
                }
            }
        } else if ((isUpArrow(e) && props.stackOrientation === 'Vertical') || (isLeftArrow(e) && props.stackOrientation === 'Horizontal')) {
            // we search for the previous sibling which is not disabled
            let targetFound = false;
            let prevTarget = targetParent.previousSibling;
            while (prevTarget && !targetFound) {
                const thisTarget = prevTarget.firstChild as HTMLInputElement;
                if (!thisTarget.disabled) {
                    targetFound = true;
                    thisTarget.focus();
                    setAsSelected(thisTarget.getAttribute('data-value') || "");
                    props.onChange(thisTarget.getAttribute('data-value') || "");
                } else {
                    prevTarget = prevTarget.previousSibling;
                }
            }
        }
    };

    const childrenWithProps = React.Children.map(props.children, (child: any) => {
        return React.cloneElement(child, { stackOrientation: props.stackOrientation, compact: props.compact });
    });

    return (
        <ParentDiv ref={divContainer} data-orientation={props.stackOrientation} role="radiogroup" onClick={handleOnClick} onKeyDown={handleKeyDown}>
            {childrenWithProps}
        </ParentDiv>
    );
};

const ParentDiv = styled.div`
    display: inline-flex;

    &[data-orientation='Vertical'] {
        flex-direction: column;
    }
`;
ParentDiv.displayName = 'ParentDiv';
